/**
 * @file This file contains the ConfigManager class, responsible for managing
 * and providing access to various game configuration settings.
 * @module ConfigManager
 */

import { buildGameConfig } from '../lib/utils.js';

/**
 * Manages and centralizes all game configuration settings. It allows
 * other parts of the game to access and modify settings like biome,
 * grid size, and path length, and builds the final game configuration
 * based on these settings.
 *
 * @export
 * @class ConfigManager
 * @typedef {ConfigManager}
 */
export class ConfigManager {
    /**
     * @param {object} configs - An object containing various game configuration objects.
     * @param {object} configs.masterItemsConfig - The master items configuration.
     * @param {object} configs.mergeVillageConfig - The base game configuration.
     * @param {object} configs.biomesConfig - The biomes configuration.
     * @param {object} configs.gameOptionsConfig - The game options configuration.
     * @param {object} configs.textConfig - The text configuration.
     */
    constructor(configs) {
        /** @private */
        this.masterItemsConfig = configs.masterItemsConfig;
        /** @private */
        this.mergeVillageConfig = configs.mergeVillageConfig;
        /** @private */
        this.biomesConfig = configs.biomesConfig;
        /** @private */
        this.gameOptionsConfig = configs.gameOptionsConfig;
        /** @private */
        this.textConfig = configs.textConfig;

        /**
         * The current game settings.
         * @type {object}
         * @property {string} biomeKey - The key of the currently selected biome.
         * @property {string} pathLength - The key of the currently selected path length.
         * @property {number} gridSize - The currently selected grid size.
         * @property {boolean} isLimitedTurnsMode - Whether limited turns mode is enabled.
         */
        this.settings = {
            biomeKey: 'default',
            pathLength: this.biomesConfig.default.defaultPathKey,
            gridSize: this.gameOptionsConfig.defaultGridSizeKey,
            isLimitedTurnsMode: this.gameOptionsConfig.isLimitedTurnsModeEnabledByDefault, // Initialize limitedTurns mode
            isAnimalsEnabled: this.gameOptionsConfig.isAnimalsEnabledByDefault, // Initialize animals enabled mode
            gameMode: 'linear', // Default game mode
            isDisastersEnabled: true,
            isRandomQueueEnabled: true,
            isNpcsEnabled: true,
            isStationaryObjectsEnabled: true,
            isPowerupsEnabled: true,
            isPowerupsEnabled: true,
            isSettlersModeEnabled: this.gameOptionsConfig.isSettlersModeEnabledByDefault,
            demoNpcSpawns: [], // New setting for demo mode
            rollingGridPrefillChance: this.gameOptionsConfig.rollingGridPrefillChance,
            isMiniMerge: false,
            babySpawnChance: this.mergeVillageConfig.mechanics.babySpawnChance, // This is not used by mini-merge, but passing it is harmless
            npcSpawnWithItemChanceIfJobsExist: this.mergeVillageConfig.mechanics.npcSpawnWithItemChanceIfJobsExist // This is not used by mini-merge, but passing it is harmless

        };
    }

    /**
     * Resets the settings to their default values.
     * This is useful when switching between different game setup flows.
     */
    resetToDefaults() {
        this.settings = {
            biomeKey: 'default',
            pathLength: this.biomesConfig.default.defaultPathKey,
            gridSize: this.gameOptionsConfig.defaultGridSizeKey,
            isLimitedTurnsMode: this.gameOptionsConfig.isLimitedTurnsModeEnabledByDefault,
            isAnimalsEnabled: this.gameOptionsConfig.isAnimalsEnabledByDefault,
            gameMode: 'linear', // Default game mode
            isDisastersEnabled: true,
            isRandomQueueEnabled: true,
            isNpcsEnabled: true,
            isStationaryObjectsEnabled: true,
            isPowerupsEnabled: true,
            isPowerupsEnabled: true,
            isSettlersModeEnabled: this.gameOptionsConfig.isSettlersModeEnabledByDefault,
            demoNpcSpawns: [],
            isMiniMerge: false,
            babySpawnChance: this.mergeVillageConfig.mechanics.babySpawnChance,
            npcSpawnWithItemChanceIfJobsExist: this.mergeVillageConfig.mechanics.npcSpawnWithItemChanceIfJobsExist
        };
    }

    /**
     * Synchronizes the manager's internal settings with a provided game configuration object.
     * This is crucial for ensuring consistency when a game is started with a specific,
     * pre-defined configuration (like from a campaign level or a save file).
     * @param {object} gameConfig - The game configuration to sync with.
     */
    syncSettings(gameConfig) {
        this.settings.biomeKey = gameConfig.biomeKey;
        this.settings.pathLength = gameConfig.pathLength;
        this.settings.gridSize = gameConfig.gridSize;
        this.settings.gameMode = gameConfig.gameMode;
        this.settings.isAnimalsEnabled = gameConfig.isAnimalsEnabled;
        this.settings.isDisastersEnabled = gameConfig.isDisastersEnabled;
        this.settings.isRandomQueueEnabled = gameConfig.isRandomQueueEnabled;
        this.settings.isNpcsEnabled = gameConfig.isNpcsEnabled;
        this.settings.isStationaryObjectsEnabled = gameConfig.isStationaryObjectsEnabled;
        this.settings.isPowerupsEnabled = gameConfig.isPowerupsEnabled;
        this.settings.isSettlersModeEnabled = gameConfig.isSettlersModeEnabled;
        this.settings.isMiniMerge = gameConfig.isMiniMerge;
        this.settings.demoNpcSpawns = gameConfig.demoNpcSpawns || [];
        this.settings.rollingGridPrefillChance = gameConfig.rollingGridPrefillChance || 0.7;
        this.settings.babySpawnChance = gameConfig.babySpawnChance || 0.25;
        this.settings.npcSpawnWithItemChanceIfJobsExist = gameConfig.npcSpawnWithItemChanceIfJobsExist || 0.5;

    }
    /**
     * Sets the active biome key in the settings.
     * Resets the path length to the default for the new biome to prevent invalid states.
     * @param {string} biomeKey - The key of the biome to set.
     */
    setBiome(biomeKey) {
        this.settings.biomeKey = biomeKey;
        const biome = this.biomesConfig[biomeKey];
        if (biome) {
            this.settings.pathLength = biome.defaultPathKey;
        }
    }

    /**
     * Sets the active path length in the settings.
     * @param {string} pathLength - The key of the path length to set.
     */
    setPathLength(pathLength) { this.settings.pathLength = pathLength; }

    /**
     * Sets the active grid size in the settings.
     * @param {number} gridSize - The grid size to set.
     */
    setGridSize(gridSize) { this.settings.gridSize = gridSize; }

    /**
     * Sets whether limited turns mode is enabled.
     * @param {boolean} enabled - True to enable limited turns mode, false to disable.
     */
    setLimitedTurns(enabled) { 
        this.settings.isLimitedTurnsMode = enabled;
    }

    /**
     * Sets whether animals are enabled.
     * @param {boolean} enabled - True to enable animals, false to disable.
     */
    setAnimalsEnabled(enabled) {
        this.settings.isAnimalsEnabled = enabled;
    }

    /**
     * Sets whether disasters are enabled.
     * @param {boolean} enabled - True to enable disasters.
     */
    setDisastersEnabled(enabled) { this.settings.isDisastersEnabled = enabled; }

    /**
     * Sets whether the item queue is random.
     * @param {boolean} enabled - True to enable random queue.
     */
    setRandomQueueEnabled(enabled) { this.settings.isRandomQueueEnabled = enabled; }

    /**
     * Sets whether NPCs are enabled.
     * @param {boolean} enabled - True to enable NPCs.
     */
    setNpcsEnabled(enabled) { this.settings.isNpcsEnabled = enabled; }

    /**
     * Sets whether stationary objects are enabled.
     * @param {boolean} enabled - True to enable stationary objects.
     */
    setStationaryObjectsEnabled(enabled) { this.settings.isStationaryObjectsEnabled = enabled; }

    /**
     * Sets whether power-ups are enabled.
     * @param {boolean} enabled - True to enable power-ups.
     */
    setPowerupsEnabled(enabled) { this.settings.isPowerupsEnabled = enabled; }

    /**
     * Sets the active game mode in the settings.
     * @param {string} gameMode - The key of the game mode to set ('linear', 'crafting', or 'rolling').
     */
    setGameMode(gameMode) {
        this.settings.gameMode = gameMode;
    }

    /**
     * Sets whether Settlers Mode is enabled.
     * @param {boolean} enabled - True to enable Settlers Mode.
     */
    setSettlersModeEnabled(enabled) {
        this.settings.isSettlersModeEnabled = enabled;
    }

    /**
     * Sets the list of forced NPC accessories for demo mode.
     * @param {string[]} accessoryKeys - An array of accessory keys to force spawn.
     */
    setDemoNpcSpawns(accessoryKeys) {
        this.settings.demoNpcSpawns = accessoryKeys;
    }

    /**
     * Gets a copy of the current game settings.
     * @returns {object} A copy of the current settings.
     */
    getSettings() {
        return { ...this.settings }; // Return a copy
    }

    /**
     * Gets the entire biomes configuration.
     * @returns {object} The biomes configuration object.
     */
    getBiomes() {
        return this.biomesConfig;
    }

    /**
     * Gets the available grid size options.
     * @returns {Array<object>} An array of grid size options.
     */
    getAvailableGridSizes() {
        return this.gameOptionsConfig.gridSizes;
    }

    /**
     * Builds and returns the active game configuration based on current settings.
     * @returns {object} A fully hydrated game configuration object.
     */
    getActiveGameConfig() {
        // Rebuild the base config with keys derived from the new masterItemsConfig structure.
        const dynamicBaseConfig = {
            ...this.mergeVillageConfig,
            animalKeys: Object.keys(this.masterItemsConfig.animals || {}),
            upgradeableGenericKeys: Object.keys(this.masterItemsConfig.special || {}),
            stationaryObjectKeys: Object.keys(this.masterItemsConfig.stationary || {}),
        };

        return buildGameConfig({
            masterItemsConfig: this.masterItemsConfig,
            baseConfig: dynamicBaseConfig,
            biomesConfig: this.biomesConfig,
            biomeKey: this.settings.biomeKey,
            pathLength: this.settings.pathLength,
            gridSize: this.settings.gridSize,
            isLimitedTurnsMode: this.settings.isLimitedTurnsMode, // Pass the boolean flag
            isAnimalsEnabled: this.settings.isAnimalsEnabled,
            defaultTurnLimit: this.gameOptionsConfig.defaultTurnLimit, // Pass the actual turn limit value
            gameMode: this.settings.gameMode, // Pass the selected game mode
            isDisastersEnabled: this.settings.isDisastersEnabled,
            isRandomQueueEnabled: this.settings.isRandomQueueEnabled,
            isNpcsEnabled: this.settings.isNpcsEnabled,
            isStationaryObjectsEnabled: this.settings.isStationaryObjectsEnabled,
            isPowerupsEnabled: this.settings.isPowerupsEnabled,
            isSettlersModeEnabled: this.settings.isSettlersModeEnabled,
            demoNpcSpawns: this.settings.demoNpcSpawns,
            isMiniMerge: this.settings.isMiniMerge,
            rollingGridPrefillChance: this.settings.rollingGridPrefillChance, // This is not used by mini-merge, but passing it is harmless
            npcSpawnWithItemChanceIfJobsExist: this.settings.npcSpawnWithItemChanceIfJobsExist
        });
    }
}